import React, { useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Logo, ThemeToggle } from "../../design-system";
import { typography } from "../../design-system/tokens";
import { AuthContext } from "../../core/context/AuthContext";
import { AffiliateAuthContext } from "../context/AffiliateAuthContext";
import { usePortalBasePath } from "../hooks/usePortalBasePath";
import { isPartnerKind, PARTNER_BASE_PATH } from "../utils/portalKind";
import { affiliateNavbarGradient } from "../utils/uiClasses";

const getTabClasses = ({ isActive }) => {
  const base = `px-3 py-1.5 rounded-md whitespace-nowrap transition-colors ${typography.bodySmall}`;
  const active = "bg-white/25 text-white shadow-sm";
  const inactive = "text-white/80 hover:bg-white/10 hover:text-white";
  return `${base} ${isActive ? active : inactive}`;
};

const AffiliateNavTabs = ({ basePath, showPayments, showSendCoupons }) => (
  <>
    <NavLink to={`${basePath}/dashboard/coupons`} end className={getTabClasses}>
      My Coupons
    </NavLink>
    <NavLink to={`${basePath}/dashboard/buy-coupons`} end className={getTabClasses}>
      Buy Coupons
    </NavLink>
    {showSendCoupons ? (
      <NavLink to={`${basePath}/dashboard/send-access-coupons`} end className={getTabClasses}>
        Send Coupons
      </NavLink>
    ) : null}
    {showPayments ? (
      <NavLink to={`${basePath}/dashboard/payments`} end className={getTabClasses}>
        My Payments
      </NavLink>
    ) : null}
    <NavLink to={`${basePath}/dashboard/analytics`} end className={getTabClasses}>
      Analytics
    </NavLink>
  </>
);

const AffiliateNavbar = ({ showTabs = true }) => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const { affiliateAuth } = useContext(AffiliateAuthContext);
  const basePath = usePortalBasePath();

  const user = affiliateAuth?.user || null;
  // While the session check is still loading, fall back to the wording of the
  // prefix the visitor is on; once the user is known, the kind wins.
  const isPartner = user ? isPartnerKind(user) : basePath === PARTNER_BASE_PATH;
  const portalTitle = isPartner ? "Partner Portal" : "Affiliate Portal";
  // "Send Coupons" appears only once this user has ever held an access coupon, and
  // then stays — the backend flag is sticky, so someone who has sent every code they
  // had keeps the tab. Hidden while the session check is still loading (no user yet)
  // so the tab does not flash in for people who should never see it.
  const hasHadAccessCoupons = Boolean(user?.has_had_access_coupons);

  const username = user?.username || null;
  const displayName = username
    ? username.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : isPartner
      ? "Partner"
      : "Affiliate";

  const handleLogout = async () => {
    await logout();
    navigate(`${basePath}/login`);
  };

  return (
    <nav className={`sticky top-0 z-50 w-full ${affiliateNavbarGradient}`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Desktop: single-row navbar with centered tabs */}
        <div className="hidden sm:flex relative justify-between items-center h-16">
          <div className="relative z-10 flex items-center gap-3 min-w-0">
            <Logo
              iconSize="w-7 h-7"
              textSize="text-xl"
              iconColor="#ffffff"
              textColor="text-white"
            />
            <button
              type="button"
              onClick={() => navigate(`${basePath}/dashboard/coupons`)}
              className={`${typography.h5Dark} hover:text-white/90 transition-colors`}
            >
              {portalTitle}
            </button>
          </div>

          {showTabs ? (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2">
                <AffiliateNavTabs
                  basePath={basePath}
                  showPayments={!isPartner}
                  showSendCoupons={hasHadAccessCoupons}
                />
              </div>
            </div>
          ) : null}

          <div className="relative z-10 flex items-center gap-3">
            <div className="flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-white">{displayName}</span>
              {username && <span className="text-xs text-white/80">@{username}</span>}
            </div>
            <ThemeToggle
              moonColor="text-white"
              sunColor="text-white"
              moonHoverFill="hover:fill-white"
              className="hover:bg-white/10"
            />
            <button
              type="button"
              className="px-3 py-1.5 rounded-full border border-white/20 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-sm font-medium transition-colors"
              onClick={handleLogout}
            >
              Log out
            </button>
          </div>
        </div>

        {/* Mobile: taller navbar — brand/actions on first row, centered tabs on second */}
        <div className="flex sm:hidden flex-col">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-2 min-w-0">
              <Logo
                iconSize="w-6 h-6"
                textSize="text-lg"
                iconColor="#ffffff"
                textColor="text-white"
              />
              <button
                type="button"
                onClick={() => navigate(`${basePath}/dashboard/coupons`)}
                className={`${typography.h5Dark} hover:text-white/90 transition-colors truncate`}
              >
                {portalTitle}
              </button>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <ThemeToggle
                moonColor="text-white"
                sunColor="text-white"
                moonHoverFill="hover:fill-white"
                className="hover:bg-white/10"
              />
              <button
                type="button"
                className="px-3 py-1.5 rounded-full border border-white/20 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-sm font-medium transition-colors"
                onClick={handleLogout}
              >
                Log out
              </button>
            </div>
          </div>

          {showTabs ? (
            <div className="flex items-center justify-center gap-2 pb-3 flex-wrap">
              <AffiliateNavTabs
                basePath={basePath}
                showPayments={!isPartner}
                showSendCoupons={hasHadAccessCoupons}
              />
            </div>
          ) : null}
        </div>
      </div>
    </nav>
  );
};

export default AffiliateNavbar;
